import React from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';

// material-ui
import {
    Button,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Tooltip,
    Box,
    CircularProgress,
    Fab,
    IconButton,
    Stack,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip,
    useTheme,
    alpha,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    LinearProgress
} from '@mui/material';

// project imports
import { useDispatch, useSelector } from 'store';
import { getExecutionsList, reprocess, bulkRedrive } from 'store/slices/stateMachines';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { MiscellaneousServices, Search, HourglassBottom, Webhook, ContentCopy, Check } from '@mui/icons-material';
import MoodBadIcon from '@mui/icons-material/MoodBad';
import { IconRotate2, IconPlayerPlay } from '@tabler/icons';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { Execution } from 'types/stateMachines';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// ==============================|| HELPER COMPONENTS ||============================== //

const StatusIcon = ({ isWaiting, status }: { isWaiting?: boolean; status: string }) => {
    if (isWaiting) return <Webhook sx={{ color: 'warning.dark', width: 30, height: 30 }} />;
    switch (status) {
        case 'SUCCEEDED':
            return <CheckCircleIcon sx={{ color: 'success.dark', width: 30, height: 30 }} />;
        case 'RUNNING':
            return <MiscellaneousServices sx={{ width: 30, height: 30 }} />;
        case 'ABORTED':
            return <MoodBadIcon sx={{ color: 'alert.dark', width: 30, height: 30 }} />;
        case 'FAILED':
        case 'ERROR':
            return <ErrorIcon sx={{ color: 'error.dark', width: 30, height: 30 }} />;
        default:
            return null;
    }
};

// ==============================|| PROCESS LIST ||============================== //

const ExecutionsListSkeleton = () => (
    <MainCard
        title={
            <Stack direction="row" alignItems="center" spacing={2}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flexGrow: 1 }}>
                    <Skeleton variant="text" width="40%" height={40} />
                    <Skeleton variant="text" width="20%" height={24} />
                </Box>
            </Stack>
        }
    >
        <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <Stack direction="row" spacing={2}>
                    <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
                    <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
                    <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
                    <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
                </Stack>
            </Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Skeleton variant="rectangular" width={20} height={20} />
                            </TableCell>
                            <TableCell>
                                <Skeleton variant="text" width={150} />
                            </TableCell>
                            <TableCell>
                                <Skeleton variant="text" width={80} />
                            </TableCell>
                            <TableCell>
                                <Skeleton variant="text" width={100} />
                            </TableCell>
                            <TableCell>
                                <Skeleton variant="text" width={120} />
                            </TableCell>
                            <TableCell>
                                <Skeleton variant="text" width={120} />
                            </TableCell>
                            <TableCell>
                                <Skeleton variant="text" width={60} />
                            </TableCell>
                            <TableCell />
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <TableRow key={i}>
                                <TableCell padding="checkbox">
                                    <Skeleton variant="rectangular" width={20} height={20} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width="80%" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 4 }} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width={100} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width={120} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width={120} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width={60} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="circular" width={24} height={24} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="circular" width={24} height={24} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </CardContent>
    </MainCard>
);

const StateMachinesExecutionsCard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [autoRefresh, setAutoRefresh] = React.useState(false);
    const [countDown, setCountDown] = React.useState(100);

    const { stateMachineId } = useParams();
    const [searchParams] = useSearchParams();
    const search = searchParams.get('search');

    // Advanced Filters State
    const [statusFilter, setStatusFilter] = React.useState('ALL');
    const [startDate, setStartDate] = React.useState<Dayjs | null>(null);
    const [endDate, setEndDate] = React.useState<Dayjs | null>(null);

    // Selection State
    const [selected, setSelected] = React.useState<string[]>([]);

    // Batch Redrive State
    const [openBatchDialog, setOpenBatchDialog] = React.useState(false);
    const [batchSize, setBatchSize] = React.useState(10);
    const [delaySeconds, setDelaySeconds] = React.useState(30);
    const [isBatchRedriving, setIsBatchRedriving] = React.useState(false);
    const [batchProgress, setBatchProgress] = React.useState({ current: 0, total: 0 });

    const { executionsData, loading, nextToken } = useSelector((state) => state.stateMachines);

    const handleLoadMore = () => {
        dispatch(
            getExecutionsList({
                stateMachineId,
                search,
                filter: statusFilter !== 'ALL' ? statusFilter : null,
                startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
                endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
                nextToken
            })
        );
    };

    const handleApplyFilters = () => {
        dispatch(
            getExecutionsList({
                stateMachineId,
                search,
                filter: statusFilter !== 'ALL' ? statusFilter : null,
                startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
                endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
                reset: true
            })
        );
    };

    const handleClearFilters = () => {
        setStatusFilter('ALL');
        setStartDate(null);
        setEndDate(null);
        dispatch(
            getExecutionsList({
                stateMachineId,
                search,
                reset: true
            })
        );
    };

    React.useEffect(() => {
        dispatch(getExecutionsList({ stateMachineId, search, reset: true }));
    }, [dispatch, stateMachineId, search]);

    const handleReprocess = async (row: Execution) => {
        dispatch(reprocess(stateMachineId as string, row.id));
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelecteds = executionsData.map((n) => n.id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }

        setSelected(newSelected);
    };

    const isSelected = (name: string) => selected.indexOf(name) !== -1;

    const redriveableSelection = React.useMemo(() => {
        const filtered = executionsData.filter(
            (row) =>
                selected.includes(row.id) &&
                (row.status === 'FAILED' || row.status === 'ERROR' || row.status === 'ABORTED' || row.status === 'TIMED_OUT')
        );
        return {
            count: filtered.length,
            ids: filtered.map((row) => row.id)
        };
    }, [selected, executionsData]);

    const handleBulkRedrive = async () => {
        if (redriveableSelection.count === 0) return;
        setOpenBatchDialog(true);
    };

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const executeBatchRedrive = async () => {
        setIsBatchRedriving(true);
        const { ids } = redriveableSelection;
        const totalBatches = Math.ceil(ids.length / batchSize);

        try {
            for (let i = 0; i < ids.length; i += batchSize) {
                const currentBatchNumber = Math.floor(i / batchSize) + 1;
                setBatchProgress({ current: currentBatchNumber, total: totalBatches });

                const batch = ids.slice(i, i + batchSize);

                // eslint-disable-next-line no-await-in-loop
                await dispatch(bulkRedrive(stateMachineId as string, batch));

                if (i + batchSize < ids.length) {
                    // eslint-disable-next-line no-await-in-loop
                    await delay(delaySeconds * 1000);
                }
            }
            setSelected([]);
            setOpenBatchDialog(false);
        } catch (err) {
            console.error(err);
        } finally {
            setIsBatchRedriving(false);
            setBatchProgress({ current: 0, total: 0 });
        }
    };

    React.useEffect(() => {
        let interval: NodeJS.Timeout;
        if (autoRefresh && !loading) {
            interval = setInterval(() => {
                setCountDown((prev) => {
                    if (prev <= 0) {
                        dispatch(
                            getExecutionsList({
                                stateMachineId,
                                search,
                                filter: statusFilter !== 'ALL' ? statusFilter : null,
                                startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
                                endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
                                reset: true
                            })
                        );
                        return 100;
                    }
                    return prev - 100 / (30 / 0.1); // 30s cycle
                });
            }, 100);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [autoRefresh, loading, dispatch, stateMachineId, search, statusFilter, startDate, endDate]);

    const [idCopied, setIdCopied] = React.useState(false);

    const handleCopyId = (id: string) => {
        navigator.clipboard.writeText(id);
        setIdCopied(true);
        setTimeout(() => setIdCopied(false), 2000);
    };

    const calculateDuration = (start?: string, end?: string) => {
        if (!start || !end) return '-';
        const diff = new Date(end).getTime() - new Date(start).getTime();
        const seconds = Math.floor(diff / 1000);
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.floor(seconds / 60);
        return `${minutes}m ${seconds % 60}s`;
    };

    const theme = useTheme();

    if (loading && executionsData.length === 0) return <ExecutionsListSkeleton />;

    const progressPercent = batchProgress.total > 0 ? (batchProgress.current / batchProgress.total) * 100 : 0;
    const progressRounded = Math.round(progressPercent);

    return (
        <MainCard
            title={
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={9}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <IconButton onClick={() => navigate('/stateMachines')} sx={{ color: theme.palette.text.primary }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            fontWeight: 900,
                                            letterSpacing: '-1px',
                                            wordBreak: 'break-all'
                                        }}
                                    >
                                        {stateMachineId}
                                    </Typography>
                                    <Tooltip title={idCopied ? 'Copied!' : 'Copy ARN'}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopyId(stateMachineId || '')}
                                            color={idCopied ? 'success' : 'primary'}
                                        >
                                            {idCopied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 0.5, opacity: 0.7 }}>
                                    Executions History
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Tooltip title="Auto Reload (30s)">
                            <Box sx={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
                                <CircularProgress value={countDown} variant="determinate" size={32} />
                                <Box
                                    sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Fab
                                        color="primary"
                                        size="small"
                                        sx={{
                                            boxShadow: 'none',
                                            width: 24,
                                            height: 24,
                                            minHeight: 24,
                                            '& svg': { width: 16, height: 16 }
                                        }}
                                        onClick={() => setAutoRefresh(!autoRefresh)}
                                    >
                                        {!autoRefresh && <PlayArrowIcon />}
                                        {autoRefresh && <PauseIcon />}
                                    </Fab>
                                </Box>
                            </Box>
                        </Tooltip>
                    </Grid>
                </Grid>
            }
        >
            <CardContent sx={{ p: 0 }}>
                <Grid container spacing={gridSpacing}>
                    {/* Advanced Filters */}
                    <Grid item xs={12}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box
                                sx={{
                                    p: 3,
                                    bgcolor:
                                        theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.common.black, 0.4)
                                            : alpha(theme.palette.primary.light, 0.4),
                                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`
                                }}
                            >
                                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="center">
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel id="status-label">Status</InputLabel>
                                        <Select
                                            labelId="status-label"
                                            value={statusFilter}
                                            label="Status"
                                            onChange={(e) => setStatusFilter(e.target.value as string)}
                                        >
                                            <MenuItem value="ALL">All Status</MenuItem>
                                            <MenuItem value="SUCCEEDED">Succeeded</MenuItem>
                                            <MenuItem value="FAILED">Failed</MenuItem>
                                            <MenuItem value="RUNNING">Running</MenuItem>
                                            <MenuItem value="WAITING_FOR_WEBHOOK">Waiting for Webhook</MenuItem>
                                            <MenuItem value="ABORTED">Aborted</MenuItem>
                                            <MenuItem value="TIMED_OUT">Timed Out</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <DatePicker
                                        label="From"
                                        value={startDate}
                                        onChange={(newValue) => setStartDate(newValue)}
                                        renderInput={(params) => <TextField {...params} size="small" />}
                                    />

                                    <DatePicker
                                        label="To"
                                        value={endDate}
                                        onChange={(newValue) => setEndDate(newValue)}
                                        renderInput={(params) => <TextField {...params} size="small" />}
                                    />

                                    <Button variant="contained" color="primary" onClick={handleApplyFilters} startIcon={<Search />}>
                                        Apply Filters
                                    </Button>
                                    <Button variant="text" color="inherit" onClick={handleClearFilters} sx={{ px: 2 }}>
                                        Clear
                                    </Button>

                                    <Box sx={{ flexGrow: 1 }} />

                                    {selected.length > 0 && redriveableSelection.count > 0 && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                                {selected.length} selected ({redriveableSelection.count} failed)
                                            </Typography>
                                            <Tooltip title="Bulk Redrive Failed Executions">
                                                <LoadingButton
                                                    variant="contained"
                                                    color="error"
                                                    size="small"
                                                    startIcon={<IconPlayerPlay />}
                                                    onClick={handleBulkRedrive}
                                                    loading={loading}
                                                >
                                                    Bulk Redrive
                                                </LoadingButton>
                                            </Tooltip>
                                        </Stack>
                                    )}
                                </Stack>
                            </Box>
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                        <TableContainer>
                            <Table sx={{ minWidth: { md: 350 } }} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={selected.length > 0 && selected.length < executionsData.length}
                                                checked={executionsData.length > 0 && selected.length === executionsData.length}
                                                onChange={handleSelectAllClick}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ pl: 3 }}>Id</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Current Step</TableCell>
                                        <TableCell>Start Date</TableCell>
                                        <TableCell>Stop Date</TableCell>
                                        <TableCell>Duration</TableCell>
                                        <TableCell />
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {executionsData.length === 0 && !loading && (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 10 }}>
                                                <Grid container direction="column" alignItems="center" spacing={1}>
                                                    <Grid item>
                                                        <Search sx={{ fontSize: 48, color: 'grey.300' }} />
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography variant="h4" color="grey.500">
                                                            No Matches Found
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography variant="body2" color="grey.400">
                                                            Try adjusting your filters or search criteria
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {executionsData.map((row, index) => {
                                        const isItemSelected = isSelected(row.id);
                                        const labelId = `enhanced-table-checkbox-${index}`;

                                        return (
                                            <TableRow
                                                hover
                                                key={index}
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                selected={isItemSelected}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isItemSelected}
                                                        onClick={(event) => handleClick(event, row.id)}
                                                        inputProps={{ 'aria-labelledby': labelId }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ pl: 3 }}>
                                                    <Grid container spacing={2} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                                                        <Grid item>
                                                            <StatusIcon isWaiting={row.isWaiting} status={row.status} />
                                                        </Grid>
                                                        <Grid item xs zeroMinWidth>
                                                            <Typography component="div" align="left" variant="subtitle1">
                                                                {row.id}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.5}>
                                                        <Chip
                                                            label={row.isWaiting ? 'Waiting for Webhook' : row.status}
                                                            size="small"
                                                            icon={row.isWaiting ? <Webhook /> : undefined}
                                                            color={(() => {
                                                                if (row.isWaiting) return 'warning';
                                                                if (row.status === 'SUCCEEDED') return 'success';
                                                                if (row.status === 'RUNNING') return 'primary';
                                                                return 'error';
                                                            })()}
                                                            variant={row.isWaiting ? 'filled' : 'outlined'}
                                                            sx={{
                                                                ...(row.isWaiting && {
                                                                    fontWeight: 700,
                                                                    animation: 'pulse 2s infinite ease-in-out',
                                                                    '@keyframes pulse': {
                                                                        '0%': { opacity: 1 },
                                                                        '50%': { opacity: 0.7 },
                                                                        '100%': { opacity: 1 }
                                                                    }
                                                                })
                                                            }}
                                                        />
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                        {row.currentState || (row.status === 'RUNNING' ? 'Initializing...' : '-')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{row.startDate && new Date(row.startDate).toLocaleString()}</TableCell>
                                                <TableCell>{row.stopDate && new Date(row.stopDate).toLocaleString()}</TableCell>
                                                <TableCell>{calculateDuration(row.startDate, row.stopDate)}</TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        component={Link}
                                                        to={`/stateMachines/${stateMachineId}/${row.id}`}
                                                        startIcon={<VisibilityIcon />}
                                                    >
                                                        Details
                                                    </Button>
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title="Start a fresh execution from the beginning with the same input. This creates a new execution record separate from the current one.">
                                                        <span>
                                                            <LoadingButton
                                                                variant="outlined"
                                                                fullWidth
                                                                color="secondary"
                                                                size="small"
                                                                startIcon={<IconRotate2 />}
                                                                disabled={row.status === 'SUCCEEDED' || row.status === 'RUNNING'}
                                                                loading={loading}
                                                                onClick={async () => handleReprocess(row)}
                                                            >
                                                                Reprocess
                                                            </LoadingButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                <Button disabled={!nextToken || loading} variant="outlined" onClick={() => handleLoadMore()}>
                    {(loading && 'loading...') || (nextToken && search && 'Keep Searching...') || 'Load More'}
                </Button>
            </Box>

            {/* Batch Redrive Dialog */}
            <Dialog open={openBatchDialog} onClose={() => !isBatchRedriving && setOpenBatchDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Bulk Redrive Executions</DialogTitle>
                <DialogContent>
                    {!isBatchRedriving ? (
                        <>
                            <DialogContentText sx={{ mb: 2 }}>
                                You are about to redrive <strong>{redriveableSelection.count}</strong> failed executions. You can configure
                                the batch size and delay between each batch to prevent overloading the system.
                            </DialogContentText>
                            <Stack spacing={2} sx={{ mt: 2 }}>
                                <TextField
                                    label="Batch Size"
                                    type="number"
                                    fullWidth
                                    value={batchSize}
                                    onChange={(e) => setBatchSize(Number(e.target.value))}
                                    inputProps={{ min: 1 }}
                                />
                                <TextField
                                    label="Delay Between Batches (seconds)"
                                    type="number"
                                    fullWidth
                                    value={delaySeconds}
                                    onChange={(e) => setDelaySeconds(Number(e.target.value))}
                                    inputProps={{ min: 0 }}
                                />
                            </Stack>
                        </>
                    ) : (
                        <Box sx={{ py: 2 }}>
                            <Typography
                                variant="h6"
                                color="warning.main"
                                gutterBottom
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <ErrorIcon /> Atención
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                                <strong>Por favor, no cierres ni recargues esta ventana</strong> hasta que el proceso termine.
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                    <LinearProgress variant="determinate" value={progressPercent} />
                                </Box>
                                <Box sx={{ minWidth: 35 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {`${progressRounded}%`}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" align="center">
                                Procesando lote {batchProgress.current} de {batchProgress.total}...
                            </Typography>
                            {batchProgress.current < batchProgress.total && (
                                <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
                                    (Esperando {delaySeconds}s para el próximo envío)
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenBatchDialog(false)} disabled={isBatchRedriving} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={executeBatchRedrive} variant="contained" disabled={isBatchRedriving} color="primary">
                        Start Redrive
                    </Button>
                </DialogActions>
            </Dialog>
        </MainCard>
    );
};

export default StateMachinesExecutionsCard;
